import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import {
  createBrand,
  deleteBrand,
  getBrands,
  updateBrand,
  type Brand,
  type CreateBrandPayload,
} from '@/api/brandApi';
import {
  createCategory,
  deleteCategory,
  getCategories,
  updateCategory,
  type Category,
  type CreateCategoryPayload,
} from '@/api/categoryApi';

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Đã xảy ra lỗi không xác định.';

export const useCatalogEntities = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isBrandLoading, setIsBrandLoading] = useState(true);
  const [isCategoryLoading, setIsCategoryLoading] = useState(true);
  const [brandError, setBrandError] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const loadCatalogEntities = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setIsBrandLoading(true);
    setIsCategoryLoading(true);
    setBrandError('');
    setCategoryError('');

    const [categoryResult, brandResult] = await Promise.allSettled([
      getCategories(),
      getBrands(),
    ]);
    if (requestId !== requestIdRef.current) return;

    if (categoryResult.status === 'fulfilled') {
      setCategories(categoryResult.value);
    } else {
      setCategoryError(getErrorMessage(categoryResult.reason));
    }

    if (brandResult.status === 'fulfilled') {
      setBrands(brandResult.value);
    } else {
      setBrandError(getErrorMessage(brandResult.reason));
    }

    setIsBrandLoading(false);
    setIsCategoryLoading(false);
  }, []);

  useEffect(() => {
    void loadCatalogEntities();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadCatalogEntities]);

  const saveBrand = async (
    input: CreateBrandPayload,
    editingBrand: Brand | null,
  ): Promise<boolean> => {
    setPendingAction('brand-save');

    try {
      if (editingBrand) {
        await updateBrand(editingBrand.id, input);
      } else {
        await createBrand(input);
      }
      message.success(editingBrand ? 'Đã cập nhật thương hiệu.' : 'Đã tạo thương hiệu mới.');
      await loadCatalogEntities();
      return true;
    } catch (error: unknown) {
      message.error(getErrorMessage(error));
      return false;
    } finally {
      setPendingAction(null);
    }
  };

  const removeBrand = async (brand: Brand): Promise<boolean> => {
    setPendingAction(`brand-delete-${brand.id}`);

    try {
      await deleteBrand(brand.id);
      message.success(`Đã xóa thương hiệu ${brand.name}.`);
      await loadCatalogEntities();
      return true;
    } catch (error: unknown) {
      message.error(getErrorMessage(error));
      return false;
    } finally {
      setPendingAction(null);
    }
  };

  const saveCategory = async (
    input: CreateCategoryPayload,
    editingCategory: Category | null,
  ): Promise<boolean> => {
    setPendingAction('category-save');

    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, input);
      } else {
        await createCategory(input);
      }
      message.success(editingCategory ? 'Đã cập nhật nhóm hàng.' : 'Đã tạo nhóm hàng mới.');
      await loadCatalogEntities();
      return true;
    } catch (error: unknown) {
      message.error(getErrorMessage(error));
      return false;
    } finally {
      setPendingAction(null);
    }
  };

  const removeCategory = async (category: Category): Promise<boolean> => {
    setPendingAction(`category-delete-${category.id}`);

    try {
      await deleteCategory(category.id);
      message.success(`Đã xóa nhóm hàng ${category.name}.`);
      await loadCatalogEntities();
      return true;
    } catch (error: unknown) {
      message.error(getErrorMessage(error));
      return false;
    } finally {
      setPendingAction(null);
    }
  };

  return {
    brands,
    categories,
    isBrandLoading,
    isCategoryLoading,
    brandError,
    categoryError,
    pendingAction,
    loadCatalogEntities,
    saveBrand,
    removeBrand,
    saveCategory,
    removeCategory,
  };
};
